import React, { useState, useEffect } from 'react';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { Ministry } from '../types';
import { apiService } from '../services/api';

const Ministries: React.FC = () => {
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMinistries = async () => {
      try {
        setLoading(true);
        const data = await apiService.getMinistries();
        setMinistries(data);
      } catch (err) {
        console.error('Error fetching ministries:', err);
        setError('Failed to load ministries');
      } finally {
        setLoading(false);
      }
    };

    fetchMinistries();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-church-light py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ministries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-church-light py-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-church-blue text-white px-4 py-2 rounded hover:bg-blue-800"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-church-light py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
            Parish Ministries
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get involved in the life of our parish through one of our many ministries.
            Each ministry offers unique opportunities to serve God and our community.
          </p>
        </header>

        {ministries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No ministries found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ministries.map((ministry) => (
              <article
                key={ministry.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
              >
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-church-dark mb-3">
                    {ministry.name}
                  </h2>
                  
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {ministry.description}
                  </p>

                  {/* Contact Person Section */}
                  {ministry.contactName && (
                    <div className="pt-6 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        Contact Person
                      </p>
                      
                      <div className="flex items-start space-x-4">
                        {/* Contact Photo */}
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 rounded-full bg-church-blue flex items-center justify-center text-white text-lg font-bold overflow-hidden">
                            {ministry.contactPhotoUrl ? (
                              <img
                                src={ministry.contactPhotoUrl}
                                alt={ministry.contactName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              ministry.contactName.split(' ').filter(n => n.trim()).map(n => n[0]).join('').substring(0, 2).toUpperCase()
                            )}
                          </div>
                        </div>

                        {/* Contact Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-church-dark mb-2">
                            {ministry.contactName}
                          </p>
                          
                          <div className="space-y-1">
                            {ministry.contactEmail && (
                              <a
                                href={`mailto:${ministry.contactEmail}`}
                                className="flex items-center text-church-blue hover:text-blue-800 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue rounded px-1 break-all"
                              >
                                <EnvelopeIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                                <span className="truncate">{ministry.contactEmail}</span>
                              </a>
                            )}
                            {ministry.contactPhone && (
                              <a
                                href={`tel:${ministry.contactPhone.replace(/[^\d]/g, '')}`}
                                className="flex items-center text-church-blue hover:text-blue-800 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue rounded px-1"
                              >
                                <PhoneIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                                {ministry.contactPhone}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-church-blue rounded-lg p-8 text-white max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold mb-4">Want to Get Involved?</h3>
            <p className="mb-6">
              We would love to help you find the perfect way to serve in our parish community. 
              Contact the ministry coordinator listed above, or reach out to our parish office to learn more.
            </p>
            <a
              href="mailto:allsaintsthirsk@rcdmidd.org.uk"
              className="bg-church-gold hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-gold focus:ring-offset-2 focus:ring-offset-church-blue inline-block"
            >
              Contact Parish Office
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ministries;
